import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    PurchaseHeaderAction,
    PurchaseHeaderActionKey,
} from './purchaseHeaderMeta';

type PurchaseHeaderActionButtonProps = {
    action: PurchaseHeaderAction;
    onAction: (key: PurchaseHeaderActionKey) => void;
    /** Shows a pending spinner + disables the button (e.g. while a template
     *  is being generated). */
    isLoading?: boolean;
};

/**
 * Single button in the Purchases page header. Split out into its own file
 * (one component per `.tsx`) so the View stays a thin list-and-map.
 * The `onPress` adapter is intentionally inline so each instance forwards
 * its own slug to the shared handler from `usePurchaseHeader`.
 */
export const PurchaseHeaderActionButton: React.FC<
    PurchaseHeaderActionButtonProps
> = ({ action, onAction, isLoading = false }) => {
    const { t } = useTranslation('pages');

    const handlePress = React.useCallback(() => {
        onAction(action.slug);
    }, [action.slug, onAction]);

    return (
        <Button
            isDisabled={isLoading}
            isPending={isLoading}
            onPress={handlePress}
            variant={action.variant}
            {...getNodeTestIdProp(`purchase-header-action-${action.slug}`)}
        >
            <Icon
                className="size-4"
                icon={action.icon}
                {...getNodeTestIdProp(
                    `purchase-header-action-icon-${action.slug}`,
                )}
            />
            {t(action.labelKey)}
        </Button>
    );
};
